/* eslint-disable no-undef */
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import "./map.css";
import { useDispatch } from "react-redux";
import { FiMapPin, FiNavigation, FiPlus, FiSave, FiSearch, FiTrash2 } from "react-icons/fi";
import { api } from "../../../api/api";
import { Toast } from "../../../utils/Toast";
import { mascaraMoeda, maskCurrency } from "../../../utils/utils";
import { UPDATE_COMPANY } from "../../../stores/reducers/CompanyReducers";
import { useCompany } from "../../../hooks/useCompany";
import { loadGMaps } from "../../../utils/loadMaps";
import { GlobalContext } from "../../../context/GlobalContext";


export function MapPage() {
  const { isAccess } = useContext(GlobalContext)

  const dispatch = useDispatch()
  const { company, configurations } = useCompany()

  const [map, setMap] = useState(null)
  const [marker, setMarker] = useState(null)
  const addressInputRef = useRef(null)
  const [addressSearch, setAddressSearch] = useState('')
  const [selectedAddress, setSelectedAddress] = useState('')
  const [isSearchingAddress, setIsSearchingAddress] = useState(false)
  const [position, setPosition] = useState({
    lat: null,
    lng: null,
  })

  const [state, setState] = useState({
    start: '',
    end: '',
    pricing: 0
  })

  const [distancesPricing, setDistancesPricing] = useState([])

  const [loadMapCompleted, setLoadMapCompleted] = useState(false)

  const applyMapLocation = useCallback((location, address = '') => {
    const nextPosition = {
      lat: typeof location.lat === 'function' ? location.lat() : location.lat,
      lng: typeof location.lng === 'function' ? location.lng() : location.lng,
    }

    setPosition(nextPosition)
    setSelectedAddress(address)
    dispatch(UPDATE_COMPANY({ location: nextPosition }))

    if (map) {
      map.panTo(nextPosition)
      map.setZoom(17)
    }
  }, [dispatch, map])

  useEffect(() => {
    if (configurations.id) {
      loadGMaps(() => {
        setLoadMapCompleted(!loadMapCompleted)
      }, configurations.googleKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations.googleKey, configurations.id])

  useEffect(() => {
    if (!map || !addressInputRef.current || !google.maps.places) return

    const autocomplete = new google.maps.places.Autocomplete(addressInputRef.current, {
      componentRestrictions: { country: 'br' },
      fields: ['formatted_address', 'geometry', 'name'],
      types: ['geocode'],
    })

    autocomplete.bindTo('bounds', map)
    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()

      if (!place.geometry?.location) {
        Toast.warning('Não foi possível localizar esse endereço')
        return
      }

      const address = place.formatted_address || place.name || addressInputRef.current?.value || ''
      setAddressSearch(address)
      applyMapLocation(place.geometry.location, address)
    })

    return () => listener.remove()
  }, [applyMapLocation, map])

  async function searchAddress() {
    const query = addressSearch.trim()
    if (!query) return Toast.warning('Digite um endereço para pesquisar')

    setIsSearchingAddress(true)

    try {
      const geocoder = new google.maps.Geocoder()
      const response = await geocoder.geocode({ address: query, region: 'BR' })
      const result = response.results?.[0]

      if (!result?.geometry?.location) {
        return Toast.warning('Endereço não encontrado. Confira os dados e tente novamente.')
      }

      const address = result.formatted_address || query
      setAddressSearch(address)
      applyMapLocation(result.geometry.location, address)
    } catch (error) {
      Toast.warning('Não foi possível pesquisar o endereço agora')
    } finally {
      setIsSearchingAddress(false)
    }
  }

  function getCurrentPosition() {
    if (navigator.geolocation) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then(function (result) {
          if (result.state === "granted") {
            navigator.geolocation.getCurrentPosition((location) => {
              setPosition({
                lat: location.coords.latitude,
                lng: location.coords.longitude
              })
              dispatch(UPDATE_COMPANY({
                location: {
                  lat: location.coords.latitude,
                  lng: location.coords.longitude
                }
              }))
            });
          } else if (result.state === "prompt") {
            navigator.geolocation.getCurrentPosition((location) => {
              setPosition({
                lat: location.coords.latitude,
                lng: location.coords.longitude
              })
              dispatch(UPDATE_COMPANY({
                location: {
                  lat: location.coords.latitude,
                  lng: location.coords.longitude
                }
              }))
            }, null, null);
          } else if (result.state === "denied") {
            navigator.geolocation.getCurrentPosition((location) => {
              setPosition({
                lat: location.coords.latitude,
                lng: location.coords.longitude
              })

              dispatch(UPDATE_COMPANY({
                location: {
                  lat: location.coords.latitude,
                  lng: location.coords.longitude
                }
              }))
            }, (err) => {
              alert(err.message)
            });
            //If denied then you have to show instructions to enable location
          }
          result.onchange = function () {
            console.log(result.state);
          };
        });
    } else {
      alert("Sorry Not available!");
    }
  }

  const initMap = useCallback(() => {
    const createMap = new google.maps.Map(document.getElementById('maps-delivery'), {
      zoom: 15,
    });

    google.maps.event.addListenerOnce(createMap, 'tilesloaded', function () {
      console.log('renderizou map')
    });

    setMap(createMap)
  }, [])

  useEffect(() => {
    if (map) {
      createMarker()
      map.setCenter(position)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, position])

  function createMarker() {
    if (marker) {
      marker.setMap(null)
    }

    const aux = new google.maps.Marker({
      position: position,
      draggable: true,
      label: {
        text: 'Origem',
        color: 'white',
        fontWeight: 'bold',
        className: 'bg-dark p-2 rounded mb-5'
      },
      title: 'Loja',
      map: map
    });

    aux.addListener('dragend', (event) => {
      setPosition({
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      })
    });

    setMarker(aux)
  }

  async function salvarLocation() {
    if (!isAccess('configuracoes', 'posicao_estabelecimento')) {
      return Toast.warning('Você não tem permissão para alterar posição do estabalecimento')
    }

    await api.put('/companies/location', {
      location: position
    })
    
    Toast.success("Localização adicionada")
  }

  async function save() {
    if (!isAccess('metodo_entrega', 'create')) {
      return Toast.warning('Você não tem permissão para criar uma entrega.')
    }

    await api.post('/distance-pricing', state)
    await findAll()
    Toast.success()
    setState({
      start: '',
      end: '',
      pricing: 0
    })
  }

  async function updated(id, key, value) {
    if (!isAccess('metodo_entrega', 'update')) {
      return Toast.warning('Você não tem permissão para atualizar entrega.')
    }

    await api.put('/distance-pricing/' + id, {
      [key]: value
    })
    await findAll()
    Toast.success()
  }

  async function findAll() {
    const response = await api.get('/distance-pricing')
    setDistancesPricing(response.data.sort((a, b) => a.start - b.start))
  }

  async function deleteDistance(id) {
    if (!isAccess('metodo_entrega', 'delete')) {
      return Toast.warning('Você não tem permissão para deletar uma entrega.')
    }

    const confirmation = await Toast.confirm()
    if (confirmation) {
      await api.delete('/distance-pricing/' + id)
      await findAll()
      return Toast.warning("Registro deletado.")
    }
  }

  useEffect(() => {
    findAll()
  }, [])

  useEffect(() => {
    if (document.getElementById('maps-delivery') && loadMapCompleted)
      initMap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMapCompleted])

  useEffect(() => {
    if (company?.location?.lat) {
      setPosition(company.location)
    } else {
      getCurrentPosition()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.location])

  return (
    <div className="distance-delivery-panel">
      <div className="map-location-toolbar">
        <div className="map-address"><span><FiMapPin size={17} /></span><div><small>Localização do estabelecimento</small><strong>{selectedAddress || `${company.address}, ${company.number}, ${company.district} - ${company.city}/${company.uf}`}</strong></div></div>
        <div className="map-location-actions">
          <button type="button" onClick={getCurrentPosition}><FiNavigation size={15} /> Usar posição atual</button>
          <button type="button" className="primary" onClick={salvarLocation}><FiSave size={15} /> Salvar localização</button>
        </div>
      </div>

      <div className="map-address-search">
        <FiSearch />
        <input
          ref={addressInputRef}
          type="search"
          value={addressSearch}
          onChange={(event) => setAddressSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              searchAddress()
            }
          }}
          placeholder="Digite rua, número, bairro e cidade"
          aria-label="Pesquisar endereço"
        />
        {addressSearch && <button type="button" className="clear" onClick={() => setAddressSearch('')} aria-label="Limpar endereço">Limpar</button>}
        <button type="button" className="search" onClick={searchAddress} disabled={isSearchingAddress}>{isSearchingAddress ? 'Buscando...' : 'Buscar endereço'}</button>
      </div>

      <div id="maps-delivery" className="delivery-map-canvas" />

      <div className="distance-pricing-layout">
        <section className="distance-form-card">
          <div className="distance-card-title"><span><FiPlus size={17} /></span><div><h3>Nova faixa de distância</h3><p>Defina o intervalo e o valor cobrado</p></div></div>
          <div className="distance-form-fields">
            <label><span>De (km)</span><input type="number" value={state.start} onChange={(event) => setState({ ...state, start: event.target.value })} placeholder="0" /></label>
            <label><span>Até (km)</span><input type="number" value={state.end} onChange={(event) => setState({ ...state, end: event.target.value })} placeholder="5" /></label>
            <label><span>Preço</span><input type="text" value={maskCurrency(state.pricing)} onChange={(event) => setState({ ...state, pricing: mascaraMoeda(event.target.value) })} placeholder="R$ 0,00" /></label>
          </div>
          <button type="button" className="distance-save-button" onClick={save}><FiPlus size={15} /> Adicionar faixa</button>
        </section>

        <section className="distance-list-card">
          <div className="distance-card-title"><span><FiNavigation size={17} /></span><div><h3>Faixas cadastradas</h3><p>{distancesPricing.length} {distancesPricing.length === 1 ? 'faixa configurada' : 'faixas configuradas'}</p></div></div>
          <div className="distance-table-wrap">
          <table className="distance-table">
            <thead>
              <tr>
                <th >De</th>
                <th >Até</th>
                <th >Preço</th>
                <th >Frete Grátis</th>
                <th >Ações</th>
              </tr>
            </thead>
            <tbody>
              {distancesPricing.sort((a, b) => a.start - b.start).map((value) => {
                return (
                  <tr key={value.id}>
                    <td data-label="De">{value.start} km</td>
                    <td data-label="Até">{value.end} km</td>
                    <td data-label="Preço"><strong>{maskCurrency(value.pricing)}</strong></td>
                    <td data-label="Frete grátis">
                      <label className="district-switch">
                        <input
                          type="checkbox"
                          checked={value.free}
                          onChange={(e) => updated(value.id, 'free', e.target.checked)}
                          role="switch"
                          aria-label={`Frete grátis de ${value.start} km até ${value.end} km`}
                        />
                        <span />
                      </label>
                    </td>
                    <td data-label="Ações">
                      <button type="button" className="distance-delete-button" onClick={() => deleteDistance(value.id)} aria-label="Excluir faixa"><FiTrash2 size={16} /></button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {distancesPricing.length === 0 && <div className="distance-empty"><FiNavigation size={22} /><strong>Nenhuma faixa cadastrada</strong><span>Adicione uma faixa de distância ao lado.</span></div>}
          </div>
        </section>
        </div>
    </div>
  );
};
